import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { Field, useFormState } from 'react-final-form';
import {
  DateField,
  RadioField,
  SimpleSelectField,
} from '@unite-us/app-components';
import formatOptions from 'common/utils/formatOptions';
import { cl2267AddNoMilitaryAffiliation } from 'common/utils/FeatureFlags/flags';

export const MILITARY_FORM_NAME = 'contact_military';

// Helper to get error for form fields
const getFieldError = (meta) => (meta.touched && meta.error ? meta.error : undefined);

// Sub-components for Service Status
function ServiceStatusFields({ militaryEnums }) {
  return (
    <>
      <Field name="military.current_status">
        {({ input, meta }) => (
          <SimpleSelectField
            id="current_status"
            label="Current Status"
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            options={formatOptions(militaryEnums.current_status)}
            optionSettings={{ labelKey: 'label', valueKey: 'value' }}
            error={getFieldError(meta)}
            searchable
            menuPortalTarget={document.body}
          />
        )}
      </Field>
      <div className="form-group">
        <div className="control-label">Currently Transitioning?</div>
        <Field name="military.currently_transitioning">
          {({ input, meta }) => (
            <RadioField
              id="transitioning"
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={formatOptions(militaryEnums.transitioning)}
              error={getFieldError(meta)}
            />
          )}
        </Field>
      </div>
      <div className="form-group">
        <div className="control-label">Has at least one day of active duty outside of basic training?</div>
        <Field name="military.at_least_one_day_active_duty">
          {({ input, meta }) => (
            <RadioField
              id="active-duty"
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={formatOptions(militaryEnums.active_duty)}
              error={getFieldError(meta)}
            />
          )}
        </Field>
      </div>
    </>
  );
}

ServiceStatusFields.propTypes = {
  militaryEnums: PropTypes.object.isRequired,
};

// Sub-component for Branch and Era
function BranchAndEraFields({ militaryEnums }) {
  return (
    <>
      <Field name="military.branch">
        {({ input, meta }) => (
          <SimpleSelectField
            id="branch"
            label="Branch Of Service"
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            options={formatOptions(militaryEnums.branch)}
            optionSettings={{ labelKey: 'label', valueKey: 'value' }}
            error={getFieldError(meta)}
            searchable
            menuPortalTarget={document.body}
          />
        )}
      </Field>
      <Field name="military.service_era">
        {({ input, meta }) => (
          <SimpleSelectField
            id="era"
            label="Service Era"
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            options={formatOptions(militaryEnums.service_era)}
            optionSettings={{ labelKey: 'label', valueKey: 'value' }}
            error={getFieldError(meta)}
            searchable
            menuPortalTarget={document.body}
          />
        )}
      </Field>
    </>
  );
}

BranchAndEraFields.propTypes = {
  militaryEnums: PropTypes.object.isRequired,
};

// Sub-component for Service Dates
function ServiceDatesFields() {
  return (
    <div className="form-group">
      <h6>Service Dates</h6>
      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <Field name="military.entry_date">
            {({ input, meta }) => (
              <DateField
                key={input.value || 'empty'}
                {...input}
                id="service-dates-start"
                label="Start Date"
                error={getFieldError(meta)}
              />
            )}
          </Field>
        </div>
        <div className="col-xs-12 col-sm-6">
          <Field name="military.exit_date">
            {({ input, meta }) => (
              <DateField
                key={input.value || 'empty'}
                {...input}
                id="service-dates-end"
                label="End Date"
                error={getFieldError(meta)}
              />
            )}
          </Field>
        </div>
      </div>
    </div>
  );
}

// Sub-component for Service Information (orchestrator)
function ServiceInformation({ militaryEnums }) {
  return (
    <>
      <ServiceStatusFields militaryEnums={militaryEnums} />
      <BranchAndEraFields militaryEnums={militaryEnums} />
      <ServiceDatesFields />
    </>
  );
}

ServiceInformation.propTypes = {
  militaryEnums: PropTypes.object.isRequired,
};

// Sub-component for Deployment Section
function DeploymentSection({ militaryEnums, isDeployed }) {
  return (
    <>
      <div className="form-group">
        <div className="control-label">Has client ever been deployed?</div>
        <Field name="military.deployed">
          {({ input, meta }) => (
            <RadioField
              id="deployed"
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={formatOptions(militaryEnums.deployed)}
              error={getFieldError(meta)}
            />
          )}
        </Field>
      </div>
      <div className={isDeployed ? '' : 'hidden'}>
        <div className="form-group">
          <h6>Deployment Dates</h6>
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <Field name="military.deployment_starts_at">
                {({ input, meta }) => (
                  <DateField
                    key={input.value || 'empty'}
                    {...input}
                    id="deployment-dates-start"
                    label="Start Date"
                    error={getFieldError(meta)}
                  />
                )}
              </Field>
            </div>
            <div className="col-xs-12 col-sm-6">
              <Field name="military.deployment_ends_at">
                {({ input, meta }) => (
                  <DateField
                    key={input.value || 'empty'}
                    {...input}
                    id="deployment-dates-end"
                    label="End Date"
                    error={getFieldError(meta)}
                  />
                )}
              </Field>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

DeploymentSection.propTypes = {
  militaryEnums: PropTypes.object.isRequired,
  isDeployed: PropTypes.bool.isRequired,
};

// Sub-component for Discharge Questions
function DischargeQuestions({ militaryEnums }) {
  return (
    <>
      <Field name="military.discharge_type">
        {({ input, meta }) => (
          <SimpleSelectField
            id="discharge"
            label="Military Discharge"
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            options={formatOptions(militaryEnums.discharge_type)}
            optionSettings={{ labelKey: 'label', valueKey: 'value' }}
            error={getFieldError(meta)}
            searchable
            menuPortalTarget={document.body}
          />
        )}
      </Field>
      <div className="form-group">
        <div className="control-label">
          Discharged or retired from military service for a disability incurred in the line of duty?
        </div>
        <Field name="military.discharged_due_to_disability">
          {({ input, meta }) => (
            <RadioField
              id="discharge-disability"
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={formatOptions(militaryEnums.discharge_disability)}
              error={getFieldError(meta)}
            />
          )}
        </Field>
      </div>
    </>
  );
}

DischargeQuestions.propTypes = {
  militaryEnums: PropTypes.object.isRequired,
};

// Reusable component for question with conditional follow-up field
function QuestionWithConditionalField({
  questionLabel,
  questionFieldName,
  questionId,
  questionOptions,
  conditionalFieldName,
  conditionalFieldId,
  conditionalFieldLabel,
  conditionalFieldOptions,
  showConditional,
}) {
  return (
    <>
      <div className="form-group">
        <div className="control-label">{questionLabel}</div>
        <Field name={questionFieldName}>
          {({ input, meta }) => (
            <RadioField
              id={questionId}
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={questionOptions}
              error={getFieldError(meta)}
            />
          )}
        </Field>
      </div>
      <div className={showConditional ? '' : 'hidden'}>
        <Field name={conditionalFieldName}>
          {({ input, meta }) => (
            <SimpleSelectField
              id={conditionalFieldId}
              label={conditionalFieldLabel}
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={conditionalFieldOptions}
              optionSettings={{ labelKey: 'label', valueKey: 'value' }}
              error={getFieldError(meta)}
              searchable
              menuPortalTarget={document.body}
            />
          )}
        </Field>
      </div>
    </>
  );
}

QuestionWithConditionalField.propTypes = {
  questionLabel: PropTypes.string.isRequired,
  questionFieldName: PropTypes.string.isRequired,
  questionId: PropTypes.string.isRequired,
  questionOptions: PropTypes.array.isRequired,
  conditionalFieldName: PropTypes.string.isRequired,
  conditionalFieldId: PropTypes.string.isRequired,
  conditionalFieldLabel: PropTypes.string.isRequired,
  conditionalFieldOptions: PropTypes.array.isRequired,
  showConditional: PropTypes.bool.isRequired,
};

// Sub-component for Discharge and Disability Section
function DischargeSection({ militaryEnums, hasDisabilityRating }) {
  return (
    <>
      <DischargeQuestions militaryEnums={militaryEnums} />
      <QuestionWithConditionalField
        questionLabel="Has VA-rated Service-Connected Disability?"
        questionFieldName="military.service_connected_disability"
        questionId="connected-disability"
        questionOptions={formatOptions(militaryEnums.connected_disability)}
        conditionalFieldName="military.service_connected_disability_rating"
        conditionalFieldId="disablility-rating"
        conditionalFieldLabel="Service-Connected Disability Rating"
        conditionalFieldOptions={formatOptions(militaryEnums.disability_rating)}
        showConditional={hasDisabilityRating}
      />
    </>
  );
}

DischargeSection.propTypes = {
  militaryEnums: PropTypes.object.isRequired,
  hasDisabilityRating: PropTypes.bool.isRequired,
};

// Sub-component for Proof of Veteran Status
function ProofSection({ militaryEnums, hasProof }) {
  return (
    <QuestionWithConditionalField
      questionLabel="Proof of veteran status?"
      questionFieldName="military.proof_of_veteran_status"
      questionId="proof"
      questionOptions={formatOptions(militaryEnums.proof_of_veteran_status)}
      conditionalFieldName="military.proof_type"
      conditionalFieldId="proof-type"
      conditionalFieldLabel="Proof Type"
      conditionalFieldOptions={formatOptions(militaryEnums.proof_type)}
      showConditional={hasProof}
    />
  );
}

ProofSection.propTypes = {
  militaryEnums: PropTypes.object.isRequired,
  hasProof: PropTypes.bool.isRequired,
};

export function MilitaryInformation({ enums, showNoMilitaryAffiliation }) {
  const { values } = useFormState({ subscription: { values: true } });

  const onAffiliationChange = () => {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 10);
  };

  const militaryEnums = _.wget(enums, 'military', {});
  // Filter out the new option if feature flag is disabled
  const affiliationOptions = showNoMilitaryAffiliation ?
    militaryEnums.affiliation : (militaryEnums.affiliation || []).filter((opt) => opt.value !== 'no_affiliation');
  const militaryData = values.military || {};
  const isMilitaryMember = militaryData.affiliation === 'military_member_or_veteran';
  const isDeployed = militaryData.deployed === 'yes';
  const hasDisabilityRating = militaryData.service_connected_disability === 'yes';
  const hasProof = militaryData.proof_of_veteran_status === 'yes';

  return (
    <div className="contact-military-information">
      <h3 className="contact-military-information__header mb-3">Military Information</h3>
      <div className="contact-military-information__content">
        <div className="military-information">
          <Field name="military.affiliation">
            {({ input, meta }) => (
              <SimpleSelectField
                id="affiliation"
                label="Affiliation"
                value={input.value}
                onChange={(value) => {
                  input.onChange(value);
                  onAffiliationChange();
                }}
                onBlur={input.onBlur}
                options={formatOptions(affiliationOptions)}
                optionSettings={{ labelKey: 'label', valueKey: 'value' }}
                error={getFieldError(meta)}
                searchable
                menuPortalTarget={document.body}
              />
            )}
          </Field>
          <div className={isMilitaryMember ? '' : 'hidden'}>
            <ServiceInformation militaryEnums={militaryEnums} />
            <DeploymentSection militaryEnums={militaryEnums} isDeployed={isDeployed} />
            <DischargeSection militaryEnums={militaryEnums} hasDisabilityRating={hasDisabilityRating} />
            <ProofSection militaryEnums={militaryEnums} hasProof={hasProof} />
          </div>
        </div>
      </div>
    </div>
  );
}

MilitaryInformation.propTypes = {
  enums: PropTypes.object.isRequired,
  showNoMilitaryAffiliation: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    enums: _.wget(state, 'session.enums.contact_data', {}),
    showNoMilitaryAffiliation: cl2267AddNoMilitaryAffiliation(state),
  };
}

MilitaryInformation.fields = [
  'military.affiliation',
  'military.current_status',
  'military.currently_transitioning',
  'military.at_least_one_day_active_duty',
  'military.branch',
  'military.service_era',
  'military.entry_date',
  'military.exit_date',
  'military.deployed',
  'military.deployment_starts_at',
  'military.deployment_ends_at',
  'military.discharge_type',
  'military.discharged_due_to_disability',
  'military.service_connected_disability',
  'military.service_connected_disability_rating',
  'military.proof_of_veteran_status',
  'military.proof_type',
];

export default connect(mapStateToProps)(MilitaryInformation);
